/* eslint-disable no-empty */
import { useCallback, useEffect, useRef, useState } from 'react';

import customAxios from '~/common/services/customAxios';
import { FavoritesCoinsProvider } from '~/common/providers/favoritesCoins';
import { BackdropProvider } from '~/common/providers/backdrop';
import { useAuth } from '~/common/providers/auth';
import { IFCWithChildren } from '~/common/interfaces/IFCWithChildren';
import { IAugustoPost } from '~/common/interfaces/IAugustoPost';
import { useMediaQuery } from '~/common/hooks/use-media-query';
import { useClickOutside } from '~/common/hooks/use-click-outisde';
import { useAppAuthData } from '~/common/hooks/use-app-auth-data';
import { vipUsers } from '~/common/constants/vip-users';

import { usePauseHoverOnScroll } from '~/common/hooks/use-pause-hover-on-scroll';

import {
  BodyContainer,
  MobileSidebarWrapper,
  PageContentWrapper,
  PrivateLayoutContainer,
} from './styles';
import { SidebarProvider } from './providers/sidebar';
import {
  PrivateLayoutProvider,
  usePrivateLayout,
} from './providers/privateLayout';
import { Sidebar } from './components/Sidebar';
import { MobileHeader } from './components/MobileHeader';
import { AugustoInsightsMobileAlertModal } from './components/AugustoInsightsMobileAlertModal';
import { AugustoInsightsModal } from './components/AugustoInsights/Modal';
import { AugustoInsights } from './components/AugustoInsights';

const BodyHOC: IFCWithChildren = ({ children }) => {
  const renderAugustoPosts = useMediaQuery('(min-width: 750px)');
  const { bodyRef } = usePrivateLayout();
  const contentRef = useRef<HTMLDivElement>(null);
  // Pausa hover/hit-testing do conteúdo enquanto a página rola (performance de
  // scroll com mouse/touchpad em todas as páginas privadas). O avatar do
  // Augusto fica FORA do wrapper e continua clicável durante o scroll.
  usePauseHoverOnScroll(contentRef);

  return (
    <BodyContainer ref={bodyRef}>
      <PageContentWrapper ref={contentRef}>{children}</PageContentWrapper>
      {renderAugustoPosts && <AugustoInsights />}
    </BodyContainer>
  );
};

export const PrivateLayout: IFCWithChildren = ({ children }) => {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState<boolean>(false);
  const [openMobileAugustoPostsModal, setOpenMobileAugustoPostsModal] =
    useState<boolean>(false);
  const isMobile = useMediaQuery('(max-width: 750px)');
  const { register: appAuthDataRegister } = useAppAuthData();
  const [
    openAugustoInsightsMobileAlertModal,
    setOpenAugustoInsightsMobileAlertModal,
  ] = useState<boolean>(false);

  const ref = useClickOutside(() => {
    setMobileSidebarOpen(false);
  });

  const {
    user: { subscription: userType },
  } = useAuth();

  const handleLoadPosts = useCallback(async () => {
    try {
      if (!userType || !vipUsers.includes(userType)) {
        return;
      }

      const { data: response } = await customAxios.get<IAugustoPost[]>(
        '/Backes/posts'
      );

      if ((response || []).length && response.some((post) => !post.isRead)) {
        setOpenAugustoInsightsMobileAlertModal(true);
      }
    } catch {}
  }, [userType]);

  useEffect(() => {
    appAuthDataRegister();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isMobile) {
      handleLoadPosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  return (
    <FavoritesCoinsProvider>
      <PrivateLayoutProvider isMobile={isMobile}>
        <BackdropProvider>
          <PrivateLayoutContainer>
            <SidebarProvider>
              {isMobile ? (
                <>
                  {openAugustoInsightsMobileAlertModal && (
                    <AugustoInsightsMobileAlertModal
                      close={() =>
                        setOpenAugustoInsightsMobileAlertModal(false)
                      }
                      closeMobileSidebar={() =>
                        setOpenAugustoInsightsMobileAlertModal(false)
                      }
                      setOpenMobileAugustoPostsModal={
                        setOpenMobileAugustoPostsModal
                      }
                    />
                  )}
                  <AugustoInsightsModal
                    open={openMobileAugustoPostsModal}
                    close={() => setOpenMobileAugustoPostsModal(false)}
                  />

                  <MobileSidebarWrapper ref={ref} open={mobileSidebarOpen}>
                    <Sidebar
                      closeMobileSidebar={() => setMobileSidebarOpen(false)}
                      setOpenMobileAugustoPostsModal={
                        setOpenMobileAugustoPostsModal
                      }
                    />
                  </MobileSidebarWrapper>
                </>
              ) : (
                <Sidebar />
              )}
            </SidebarProvider>

            {isMobile && (
              <MobileHeader openSidebar={() => setMobileSidebarOpen(true)} />
            )}

            <BodyHOC>{children}</BodyHOC>
          </PrivateLayoutContainer>
        </BackdropProvider>
      </PrivateLayoutProvider>
    </FavoritesCoinsProvider>
  );
};
